import axios from "axios";
import { createHash } from "utils/createHash";

import { db, auth } from "config/firebase";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  deleteField,
} from "firebase/firestore";

import * as geofire from "geofire-common";

async function addLocationFields(gameData: any): Promise<any> {
  // get geocords from full address
  try {
    console.log("=== GEOCODING API CALL ===");
    console.log("Address:", gameData.address);
    console.log("Zipcode:", gameData.zipcode);

    // Use full address if available, otherwise fallback to zipcode
    const addressToGeocode = gameData.address || gameData.zipcode;
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressToGeocode)}&key=AIzaSyDoG4eWlZpyV4bUijIbWZWEgwuw60f6fEE`;
    console.log("API URL:", apiUrl);

    const res = await axios.get(apiUrl);
    console.log("Geocoding API response status:", res.status);
    console.log("Geocoding API response data type:", typeof res.data);
    console.log(
      "Geocoding API response keys:",
      res.data ? Object.keys(res.data) : "null/undefined",
    );
    console.log(
      "Geocoding API full response:",
      JSON.stringify(res.data, null, 2),
    );

    if (!res.data) {
      throw new Error("Empty response from geocoding API");
    }

    if (res.data.error_message) {
      throw new Error(`Geocoding API error: ${res.data.error_message}`);
    }

    if (!res.data.results || res.data.results.length === 0) {
      console.error(
        "No geocoding results found for address:",
        addressToGeocode,
      );
      console.error("API status:", res.data.status);
      console.error("API response:", res.data);
      throw new Error(
        `No geocoding results found for address: ${addressToGeocode}. API status: ${res.data.status}`,
      );
    }

    const addressComponents = res.data.results[0].address_components;
    console.log(`addressComponents`, addressComponents);
    const last = addressComponents.length - 1;
    const updatedGameData = { ...gameData };
    updatedGameData.country = addressComponents[last];
    const fetchedState = addressComponents[last - 1];
    console.log(`fetchedState`, fetchedState);
    if (fetchedState !== gameData.state) {
      // TODO: warn user that wrong state is input
      console.log("wrong state");
    }
    const { lat, lng } = res.data.results[0].geometry.location;
    updatedGameData.lat = lat;
    updatedGameData.lng = lng;
    updatedGameData.geohash = geofire.geohashForLocation([lat, lng]);
    console.log("Successfully added location fields");
    return updatedGameData;
  } catch (err: any) {
    console.error("=== GEOCODING ERROR ===");
    console.error("Error type:", err.constructor?.name);
    console.error("Error message:", err.message);
    console.error("Error response:", err.response?.data);
    console.error("Error status:", err.response?.status);
    console.error("Full error:", err);
    throw err;
  }
}

// Helper functions for createGame
const validateAuthentication = (currentUser: any, gameData: any) => {
  if (!currentUser) {
    return {
      isValid: false,
      error: {
        status: "ERROR",
        message: "User must be authenticated to create a game",
      },
    };
  }

  if (currentUser.uid !== gameData.hostId) {
    return {
      isValid: false,
      error: {
        status: "ERROR",
        message: "User ID does not match host ID",
      },
    };
  }

  return { isValid: true };
};

const validateHostPrivileges = async (currentUser: any) => {
  const idTokenResult = await currentUser.getIdTokenResult(true);

  if (!idTokenResult.claims.isHost) {
    return {
      isValid: false,
      error: {
        status: "ERROR",
        message:
          "You don't have host privileges. Please ensure you've registered as a host and signed out/in to refresh your permissions.",
      },
    };
  }

  return { isValid: true };
};

const checkAccessCodeCollision = async (gameData: any) => {
  if (gameData.access !== "Private" || !gameData.accesscode) {
    return { hasCollision: false };
  }

  const accessCodeQuery = query(
    collection(db, "games"),
    where("access", "==", "Private"),
    where("accesscode", "==", gameData.accesscode),
  );

  const existingGamesSnap = await getDocs(accessCodeQuery);

  if (!existingGamesSnap.empty) {
    return {
      hasCollision: true,
      error: {
        status: "ERROR",
        message: `Access code "${gameData.accesscode}" is already in use. Please choose a different access code.`,
      },
    };
  }

  return { hasCollision: false };
};

const checkGameIdCollision = async (gameId: string) => {
  const existingGameRef = doc(db, "games", gameId);
  const existingGameSnap = await getDoc(existingGameRef);

  if (existingGameSnap.exists()) {
    return {
      hasCollision: true,
      error: {
        status: "ERROR",
        message:
          "A game with this configuration already exists. Please try again.",
      },
    };
  }

  return { hasCollision: false };
};

const createGameDocument = async (gameData: any, gameId: string) => {
  const updatedGameData = await addLocationFields(gameData);

  const finalGameData = {
    ...updatedGameData,
    id: gameId,
    waitlist: {},
    currentPlayers: {},
    playerlist: {},
  };

  const gameDocRef = doc(db, "games", gameId);
  await setDoc(gameDocRef, finalGameData);

  return finalGameData;
};

const handleCreateGameError = (err: any) => {
  console.error("Error Creating Game:", err);

  if (err.message === "User does not have host privileges") {
    return {
      status: "ERROR",
      message:
        "You must be a registered host to create games. If you recently registered as a host, please sign out and sign back in to refresh your permissions.",
    };
  }

  if (err.code === "permission-denied") {
    return {
      status: "ERROR",
      message:
        "Permission denied. Please ensure you are logged in as a host. If you recently registered as a host, try signing out and signing back in.",
    };
  }

  return {
    status: "ERROR",
    message: err.message || "Error creating game",
  };
};

export const createGame = async ({
  gameData,
  finalCB = () => {},
}: {
  gameData: any;
  finalCB?: () => void;
}): Promise<any> => {
  try {
    const { currentUser } = auth;

    // Step 1: Validate authentication
    const authValidation = validateAuthentication(currentUser, gameData);
    if (!authValidation.isValid) {
      return authValidation.error;
    }

    // Step 2: Validate host privileges
    const hostValidation = await validateHostPrivileges(currentUser);
    if (!hostValidation.isValid) {
      return hostValidation.error;
    }

    // Step 3: Generate game ID
    const gameId = gameData.id || createHash(Date.now() + gameData.hostId);

    // Step 4: Check for collisions
    const accessCodeCheck = await checkAccessCodeCollision(gameData);
    if (accessCodeCheck.hasCollision) {
      return accessCodeCheck.error;
    }

    const gameIdCheck = await checkGameIdCollision(gameId);
    if (gameIdCheck.hasCollision) {
      return gameIdCheck.error;
    }

    // Step 5: Create the game document
    await createGameDocument(gameData, gameId);

    // Step 6: Success
    finalCB();
    return {
      status: "SUCCESS",
      message: "Game Created Successfully",
    };
  } catch (err: any) {
    return handleCreateGameError(err);
  } finally {
    // Ensure finalCB is called even if there's an error
    if (finalCB) {
      finalCB();
    }
  }
};

export const editGame = async ({
  gameData,
  finalCB,
}: {
  gameData: any;
  finalCB: () => void;
}): Promise<any> => {
  try {
    const gameId = gameData.id;
    await setDoc(doc(db, "games", gameId), gameData, { merge: true });
    return {
      status: "SUCCESS",
      message: "Game Details Updated Successfully",
    };
  } catch (err) {
    console.log("Error Updating Game Details: ", err);
    return {
      status: "ERROR",
      message: `Error Updating Game Details`,
    };
  } finally {
    finalCB();
  }
};

const deleteAllRequests = async (gameId: string): Promise<void> => {
  try {
    const q = query(collection(db, "requests"), where("gameId", "==", gameId));
    const querySnapshot = await getDocs(q);

    const deletePromises = querySnapshot.docs.map((docSnapshot) =>
      deleteDoc(docSnapshot.ref),
    );

    await Promise.all(deletePromises);
  } catch (err) {
    console.log(err);
  }
};

export const deleteGame = async (gameId: string): Promise<void> => {
  await deleteDoc(doc(db, "games", gameId));
  await deleteAllRequests(gameId);
};

export const removePlayerFromPrivateGame = async ({
  gameId,
  playerId,
  hostId,
}: {
  gameId: string;
  playerId: string;
  hostId: string;
}): Promise<any> => {
  try {
    const { currentUser } = auth;

    if (!currentUser) {
      return {
        status: "ERROR",
        message: "User must be authenticated to remove players",
      };
    }

    if (currentUser.uid !== hostId) {
      return {
        status: "ERROR",
        message: "Only the game host can remove players from private games",
      };
    }

    const gameRef = doc(db, "games", gameId);
    const gameSnap = await getDoc(gameRef);

    if (!gameSnap.exists()) {
      return {
        status: "ERROR",
        message: "Game not found",
      };
    }

    const gameData = gameSnap.data();

    if (gameData.access !== "Private") {
      return {
        status: "ERROR",
        message: "This function only works for private games",
      };
    }

    if (gameData.hostId !== hostId) {
      return {
        status: "ERROR",
        message: "You are not the host of this game",
      };
    }

    if (!gameData.allowedPlayers || !gameData.allowedPlayers[playerId]) {
      return {
        status: "ERROR",
        message: "Player is not in the allowed players list",
      };
    }

    await updateDoc(gameRef, {
      [`allowedPlayers.${playerId}`]: deleteField(),
    });

    return {
      status: "SUCCESS",
      message: "Player removed from private game successfully",
    };
  } catch (err: any) {
    console.error("Error removing player from private game:", err);
    return {
      status: "ERROR",
      message: err.message || "Error removing player from private game",
    };
  }
};

const startGame = async (gameId: string): Promise<any> => {
  try {
    const startedAt = Date.now();
    const sessionId = createHash(gameId + startedAt);

    await updateDoc(doc(db, "games", gameId), { startedAt, sessionId });
    return { startedAt, sessionId };
  } catch (err) {
    console.log("Error updating game start time", err);
    return err;
  }
};

const changeGameStatus = async (
  gameId: string,
  status: string,
): Promise<any> => {
  try {
    await updateDoc(doc(db, "games", gameId), {
      status,
      timeStamp: Date.now(),
    });
    return "success";
  } catch (err) {
    return err;
  }
};

const endGame = async (gameId: string): Promise<any> => {
  try {
    const prom1 = updateDoc(doc(db, "games", gameId), {
      waitlist: {},
      currentPlayers: {},
      playerlist: {},
    });
    const prom2 = deleteAllRequests(gameId);

    await Promise.all([prom1, prom2]);
    return "success";
  } catch (err) {
    return err;
  }
};

export default {
  createGame,
  editGame,
  deleteGame,
  removePlayerFromPrivateGame,
  startGame,
  changeGameStatus,
  endGame,
};
